import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { MagnifyingGlassIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { agentsApi, categoriesApi, clientsApi } from '../services/api';
import { PhotoQuery } from '../types';
import { useDebounce } from '../hooks/useDebounce';

interface FiltersProps {
  onFiltersChange: (filters: PhotoQuery) => void;
  initialFilters?: PhotoQuery;
}

const Filters: React.FC<FiltersProps> = ({ onFiltersChange, initialFilters = {} }) => {
  const [filters, setFilters] = useState<PhotoQuery>(initialFilters);
  const [searchInput, setSearchInput] = useState<string>(initialFilters.search || '');
  const [showFilters, setShowFilters] = useState(false);

  const { data: agents = [] } = useQuery('agents', agentsApi.getAgents);
  const { data: categories = [] } = useQuery('categories', categoriesApi.getCategories);
  const { data: clients = [] } = useQuery('clients', () => clientsApi.getClients());

  // Debounce search input to avoid excessive API calls
  const debouncedSearch = useDebounce(searchInput, 500);

  // Update search in filters when debounced value changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      search: debouncedSearch || undefined,
      page: 1 // Reset to first page when search changes
    }));
  }, [debouncedSearch]);

  // Update local state when initialFilters change from parent (e.g., page changes)
  useEffect(() => {
    // Only update if page changed from parent, not other filters
    if (initialFilters.page !== filters.page &&
        Object.keys(initialFilters).length === Object.keys(filters).length) {
      setFilters(prev => ({ ...prev, page: initialFilters.page || 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters.page]);

  // Call parent callback when filters change, but debounce non-search filters
  useEffect(() => {
    const timer = setTimeout(() => {
      onFiltersChange(filters);
    }, 50); // Small delay to batch updates

    return () => clearTimeout(timer);
  }, [filters, onFiltersChange]);

  const handleFilterChange = (key: keyof PhotoQuery, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value === '' ? undefined : value,
      // Only reset to first page when actual filters change, not pagination
      ...(key !== 'page' && { page: 1 })
    }));
  };

  const clearFilters = () => {
    const newFilters = { page: 1, limit: filters.limit || 20 };
    setFilters(newFilters);
    setSearchInput('');
    // Force parent update even if filters are the same
    onFiltersChange(newFilters);
  };

  const hasActiveFilters = Object.keys(filters).some(
    key => key !== 'page' && key !== 'limit' && filters[key as keyof PhotoQuery]
  ) || searchInput.trim() !== '';

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      {/* Search and Filter Toggle */}
      <div className="flex flex-col sm:flex-row gap-4 items-center">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            placeholder="Поиск по клиентам, агентам, категориям..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              showFilters || hasActiveFilters
                ? 'bg-primary-100 text-primary-700 border border-primary-200'
                : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
            }`}
          >
            <FunnelIcon className="h-4 w-4" />
            Фильтры
            {hasActiveFilters && (
              <span className="bg-primary-500 text-white rounded-full text-xs px-2 py-0.5">
                {Object.keys(filters).filter(key =>
                  key !== 'page' && key !== 'limit' && filters[key as keyof PhotoQuery]
                ).length + (searchInput.trim() !== '' ? 1 : 0)}
              </span>
            )}
          </button>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              <XMarkIcon className="h-4 w-4" />
              Очистить
            </button>
          )}
        </div>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Agent Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Торговый представитель
              </label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                value={filters.agentId || ''}
                onChange={(e) => handleFilterChange('agentId', e.target.value ? parseInt(e.target.value) : undefined)}
              >
                <option value="">Все агенты</option>
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name} ({agent.photo_count || 0})
                  </option>
                ))}
              </select>
            </div>

            {/* Client Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Клиент
              </label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                value={filters.clientId || ''}
                onChange={(e) => handleFilterChange('clientId', e.target.value ? parseInt(e.target.value) : undefined)}
              >
                <option value="">Все клиенты</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name} ({client.photo_count || 0})
                  </option>
                ))}
              </select>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Категория
              </label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                value={filters.categoryId || ''}
                onChange={(e) => handleFilterChange('categoryId', e.target.value ? parseInt(e.target.value) : undefined)}
              >
                <option value="">Все категории</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name} ({category.photo_count || 0})
                  </option>
                ))}
              </select>
            </div>

            {/* Date From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Дата с
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                value={filters.dateFrom || ''}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Дата до
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                value={filters.dateTo || ''}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Filters;